import React, { useState } from 'react';
import { formatearMoneda, limpiarMoneda } from '../utils/formatMoney';

const dateInputValue = (v) => v ? String(v).slice(0, 10) : '';
const dateDisplay = (v) => {
  if (!v) return '—';
  const [y, m, d] = String(v).slice(0, 10).split('-');
  return `${d}-${m}-${y}`;
};

const CHECKLIST = [
  { campo: 'chk_cuenta_domiciliar', label: 'Cuenta a Domiciliar' },
  { campo: 'chk_biometria_huellas', label: 'Biometría Huellas' },
  { campo: 'chk_aviso_avaluo', label: 'Aviso de Avalúo' },
  { campo: 'chk_visita_domiciliaria', label: 'Visita Domiciliaria' },
  { campo: 'chk_documentos_notaria', label: 'Documentos Notaría' },
  { campo: 'chk_documentos_banco', label: 'Documentos Banco' },
];

export const CAMPOS_EDITABLES = [
  'status_credito_id', 'fecha_vigencia', 'tasa_credito', 'comision_apertura', 'monto_autorizado',
  'fecha_programacion_firma', 'fecha_firma_real', 'monto_credito_firmado', 'motivo_rechazo', 'motivo_desistimiento',
  'chk_cuenta_domiciliar', 'chk_biometria_huellas', 'chk_aviso_avaluo', 'chk_visita_domiciliaria',
  'chk_documentos_notaria', 'chk_documentos_banco', 'chk_documentos_infonavit',
];

export const copiaEditable = (banco) => {
  const copia = {};
  CAMPOS_EDITABLES.forEach(c => { copia[c] = banco[c]; });
  return copia;
};

const st = {
  card: { backgroundColor: '#F9FAFB', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 },
  bancoNombre: { fontSize: 16, fontWeight: 'bold', color: '#1a3a6e', margin: 0 },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 12 },
  gridFormalizar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 12 },
  field: { marginBottom: 4 },
  label: { display: 'block', fontSize: 12, fontWeight: '600', color: '#6b7280', marginBottom: 4 },
  value: { fontSize: 14, color: '#111827' },
  input: { width: '100%', height: 36, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
  textarea: { width: '100%', minHeight: 70, padding: '8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' },
  moneyWrap: { position: 'relative' },
  moneySign: { position: 'absolute', left: 8, top: 8, color: '#6b7280', fontSize: 13 },
  moneyInput: { width: '100%', height: 36, padding: '6px 8px 6px 18px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
  checklistWrap: { display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' },
  checkLabel: { fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' },
  btnDoc: { padding: '7px 14px', backgroundColor: 'white', color: '#1a3a6e', border: '1px solid #1a3a6e', borderRadius: 6, cursor: 'pointer', fontSize: 12.5, fontWeight: '600' },
  docWrap: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  docNombre: { fontSize: 12.5, color: '#374151' },
};

const API = '/api';

// En Avaluo(6) … Cobrado(12): solo el "Banco a Formalizar" puede tener estos status.
const STATUS_FORMALIZACION = [6, 7, 8, 9, 10, 11, 12];

const SeguimientoBancoCard = ({ banco, local, onChange, statusOptions, mostrarChecklist, onDocumentoActualizado, soloHastaAutorizado }) => {
  const [subiendoDoc, setSubiendoDoc] = useState(false);
  const [errorDoc, setErrorDoc] = useState('');

  const l = local || copiaEditable(banco);
  const upd = (patch) => onChange(patch);

  const subirDocumento = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { setErrorDoc('Solo se permiten archivos PDF'); return; }
    setErrorDoc('');
    setSubiendoDoc(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await fetch(`${API}/seguimiento-bancos/${banco.id}/documento-autorizacion`, { method: 'POST', body: form });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErrorDoc(d.error || 'Error al subir el documento');
        return;
      }
      const actualizado = await r.json();
      onDocumentoActualizado(banco.id, actualizado);
    } finally {
      setSubiendoDoc(false);
    }
  };

  const verDocumento = async () => {
    const r = await fetch(`${API}/seguimiento-bancos/${banco.id}/documento-autorizacion`);
    if (!r.ok) { setErrorDoc('No se pudo abrir el documento'); return; }
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), '_blank');
  };

  const statusNombreLocal = statusOptions.find(s => s.id === l.status_credito_id)?.nombre;

  return (
    <div style={st.card}>
      <div style={st.header}>
        <h4 style={st.bancoNombre}>{banco.banco}</h4>
        <select
          value={l.status_credito_id}
          onChange={e => upd({ status_credito_id: Number(e.target.value) })}
          style={{ ...st.input, width: 200 }}
        >
          {statusOptions
            // Fuera de "Banco a Formalizar" el máximo es Autorizado; se conserva el
            // status guardado (casos legacy ya avanzados) para que el select no quede en blanco.
            .filter(s => !soloHastaAutorizado || !STATUS_FORMALIZACION.includes(s.id) || s.id === banco.status_credito_id)
            .map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
        </select>
      </div>

      {/* mostrarChecklist solo llega en true para el recuadro "Banco a Formalizar"
          (ver SeguimientoClienteDetalle) — se usa aquí también para distinguir ese
          recuadro, que ya está en Avalúo o más adelante, y por eso muestra Monto
          Autorizado + Fecha de Vigencia en vez de Valor Solicitado. */}
      <div style={mostrarChecklist ? st.gridFormalizar : st.grid4}>
        <div style={st.field}><label style={st.label}>Tipo de Crédito</label><div style={st.value}>{banco.tipo_credito || '—'}</div></div>
        <div style={st.field}><label style={st.label}>Esquema de Crédito</label><div style={st.value}>{banco.esquema_credito || '—'}</div></div>
        <div style={st.field}><label style={st.label}>Plazo</label><div style={st.value}>{banco.plazo_credito ? `${banco.plazo_credito} años` : '—'}</div></div>
        <div style={st.field}><label style={st.label}>Valor Inmueble</label><div style={st.value}>{banco.valor_inmueble ? `$${formatearMoneda(banco.valor_inmueble)}` : '—'}</div></div>
        {mostrarChecklist ? (
          <>
            <div style={st.field}><label style={st.label}>Monto Autorizado</label><div style={st.value}>{banco.monto_autorizado ? `$${formatearMoneda(banco.monto_autorizado)}` : '—'}</div></div>
            <div style={st.field}><label style={st.label}>Fecha de Vigencia</label><div style={st.value}>{dateDisplay(banco.fecha_vigencia)}</div></div>
          </>
        ) : (
          <div style={st.field}><label style={st.label}>Valor Solicitado</label><div style={st.value}>{banco.monto_solicitado ? `$${formatearMoneda(banco.monto_solicitado)}` : '—'}</div></div>
        )}
        <div style={st.field}><label style={st.label}>Fecha de Ingreso</label><div style={st.value}>{dateDisplay(banco.fecha_ingreso)}</div></div>
        <div style={st.field}><label style={st.label}>Último Cambio de Status</label><div style={st.value}>{dateDisplay(banco.fecha_ultimo_status)}</div></div>
      </div>

      {statusNombreLocal === 'Autorizado' && (
        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Fecha de Vigencia</label>
            <input type="date" value={dateInputValue(l.fecha_vigencia)} onChange={e => upd({ fecha_vigencia: e.target.value })} style={st.input} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Tasa Crédito (%)</label>
            <input value={formatearMoneda(l.tasa_credito, 2)} onChange={e => upd({ tasa_credito: limpiarMoneda(e.target.value, 2) })} style={st.input} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Comisión Apertura (%)</label>
            <input value={formatearMoneda(l.comision_apertura, 2)} onChange={e => upd({ comision_apertura: limpiarMoneda(e.target.value, 2) })} style={st.input} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Monto Autorizado</label>
            <div style={st.moneyWrap}>
              <span style={st.moneySign}>$</span>
              <input value={formatearMoneda(l.monto_autorizado)} onChange={e => upd({ monto_autorizado: limpiarMoneda(e.target.value) })} style={st.moneyInput} />
            </div>
          </div>
          <div style={{ ...st.field, gridColumn: '1 / -1' }}>
            <label style={st.label}>Autorización de Crédito (PDF)</label>
            <div style={st.docWrap}>
              <label style={st.btnDoc}>
                {subiendoDoc ? 'Subiendo...' : (banco.documento_autorizacion_nombre ? 'Reemplazar PDF' : 'Subir PDF')}
                <input type="file" accept="application/pdf" style={{ display: 'none' }}
                  disabled={subiendoDoc}
                  onChange={e => { subirDocumento(e.target.files[0]); e.target.value = ''; }} />
              </label>
              {banco.documento_autorizacion_nombre && (
                <>
                  {/* <a> y no <button>: sigue activo dentro del <fieldset disabled> de solo lectura (ver PDF es lectura). */}
                  <a href="#ver-pdf" style={{ ...st.btnDoc, textDecoration: 'none', display: 'inline-block' }} onClick={e => { e.preventDefault(); verDocumento(); }}>Ver PDF</a>
                  <span style={st.docNombre}>{banco.documento_autorizacion_nombre}</span>
                </>
              )}
            </div>
            {errorDoc && <div style={{ color: '#dc2626', fontSize: 12.5, marginTop: 4 }}>{errorDoc}</div>}
          </div>
        </div>
      )}

      {statusNombreLocal === 'Por Firmar' && (
        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Fecha de Programación Firma</label>
            <input type="date" value={dateInputValue(l.fecha_programacion_firma)} onChange={e => upd({ fecha_programacion_firma: e.target.value })} style={st.input} />
          </div>
        </div>
      )}

      {['Firmado', 'Por Cobrar', 'Cobrado'].includes(statusNombreLocal) && (
        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Fecha Real de Firma</label>
            <input type="date" value={dateInputValue(l.fecha_firma_real)} onChange={e => upd({ fecha_firma_real: e.target.value })} style={st.input} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Monto de Crédito Firmado</label>
            <div style={st.moneyWrap}>
              <span style={st.moneySign}>$</span>
              <input value={formatearMoneda(l.monto_credito_firmado)} onChange={e => upd({ monto_credito_firmado: limpiarMoneda(e.target.value) })} style={st.moneyInput} />
            </div>
          </div>
        </div>
      )}

      {statusNombreLocal === 'Rechazado' && (
        <div style={st.field}>
          <label style={st.label}>Razón del Rechazo</label>
          <textarea value={l.motivo_rechazo || ''} onChange={e => upd({ motivo_rechazo: e.target.value })} style={st.textarea} />
        </div>
      )}

      {statusNombreLocal === 'Desistimiento' && (
        <div style={st.field}>
          <label style={st.label}>Motivo del Desistimiento</label>
          <textarea value={l.motivo_desistimiento || ''} onChange={e => upd({ motivo_desistimiento: e.target.value })} style={st.textarea} />
        </div>
      )}

      {mostrarChecklist && (
        <div style={st.checklistWrap}>
          {CHECKLIST.map(({ campo, label }) => (
            <label key={campo} style={st.checkLabel}>
              <input type="checkbox" checked={!!l[campo]} onChange={e => upd({ [campo]: e.target.checked })} />
              {label}
            </label>
          ))}
          {banco.esquema_credito === 'Cofinavit' && (
            <label style={st.checkLabel}>
              <input type="checkbox" checked={!!l.chk_documentos_infonavit} onChange={e => upd({ chk_documentos_infonavit: e.target.checked })} />
              Documentos Infonavit
            </label>
          )}
        </div>
      )}
    </div>
  );
};

export default SeguimientoBancoCard;
